﻿/**
 * ECharts K线图表配置工具
 * 专业股票图表样式 - 参考专业交易软件设计
 */
import * as echarts from 'echarts'

/**
 * 确保 ECharts 蜡烛图（candlestick）上下影线 100% 居中于实体
 * 彻底修复 ECharts 官方在亚像素栅格化（subPixelOptimize）时，
 * 影线固定向左/向右吸附导致在手机等窄屏/高DPI下影线贴在实体边缘的视觉 Bug。
 */
let isCandlestickCenteredRegistered = false

export function ensureCandlestickCentered() {
  if (isCandlestickCenteredRegistered) return
  isCandlestickCenteredRegistered = true

  try {
    const priority = (echarts.PRIORITY && echarts.PRIORITY.VISUAL && echarts.PRIORITY.VISUAL.LAYOUT)
      ? echarts.PRIORITY.VISUAL.LAYOUT + 50
      : 1050

    echarts.registerLayout(priority, function (ecModel) {
      ecModel.eachSeriesByType('candlestick', function (seriesModel) {
        const data = seriesModel.getData()
        if (!data) return
        const count = data.count()
        for (let i = 0; i < count; i++) {
          const itemLayout = data.getItemLayout(i)
          if (itemLayout && Array.isArray(itemLayout.ends) && itemLayout.ends.length >= 8) {
            // ends[0]: [xRight, yTop], ends[1]: [xLeft, yTop]
            // ends[2]: [xLeft, yBottom], ends[3]: [xRight, yBottom]
            // ends[4]: [xHigh, yHigh], ends[5]: [xTopBody, yTopBody] (上影线)
            // ends[6]: [xLow, yLow], ends[7]: [xBottomBody, yBottomBody] (下影线)
            const xRight = itemLayout.ends[0][0]
            const xLeft = itemLayout.ends[1][0]
            if (typeof xRight === 'number' && typeof xLeft === 'number' && !isNaN(xRight) && !isNaN(xLeft)) {
              const exactCenter = (xRight + xLeft) / 2
              itemLayout.ends[4][0] = exactCenter
              itemLayout.ends[5][0] = exactCenter
              itemLayout.ends[6][0] = exactCenter
              itemLayout.ends[7][0] = exactCenter
            }
          }
        }
      })
    })
  } catch (err) {
    console.warn('[echartsKlineConfig] 注册蜡烛图影线居中布局异常:', err)
  }
}

// 自动执行全局单例注册
ensureCandlestickCentered()

/**
 * 确保 ECharts 蜡烛图与箱线图数据安全，防止 null 项导致 WhiskerBoxCommonMixin 报错
 * 当 xAxis 为 category 类型时，ECharts 会遍历 series.data 为每项调用 item.unshift 或访问 item.value。
 * 若 data 包含 null，会触发 TypeError: Cannot read properties of null (reading 'value')。
 * 此预处理器自动将 null / undefined 占位项安全规范化为 [null, null, null, null]。
 */
let isWhiskerBoxSanitizerRegistered = false

export function ensureCandlestickDataSanitized() {
  if (isWhiskerBoxSanitizerRegistered) return
  isWhiskerBoxSanitizerRegistered = true

  try {
    echarts.registerPreprocessor(function (option) {
      if (!option || !Array.isArray(option.series)) return
      option.series.forEach(series => {
        if (series && (series.type === 'candlestick' || series.type === 'k' || series.type === 'boxplot')) {
          if (Array.isArray(series.data)) {
            for (let i = 0; i < series.data.length; i++) {
              if (series.data[i] == null) {
                series.data[i] = [null, null, null, null]
              }
            }
          }
        }
      })
    })
  } catch (err) {
    console.warn('[echartsKlineConfig] 注册蜡烛图数据预处理器异常:', err)
  }
}

// 自动执行全局单例注册
ensureCandlestickDataSanitized()

/**
 * 获取专业K线图表配置
 * @param {Object} options - 配置选项
 * @param {boolean} options.isDark - 是否为深色主题
 * @param {boolean} options.showMA - 是否显示MA线
 * @param {Array} options.maParams - MA参数 [5, 10, 20]
 * @param {boolean} options.showVolume - 是否显示成交量
 * @param {boolean} options.showLimitLines - 是否显示涨跌停线（仅5分钟图）
 * @param {number} options.limitUp - 涨停价
 * @param {number} options.limitDown - 跌停价
 * @param {number} options.prevClose - 前收盘价
 * @returns {Object} ECharts配置对象
 */
export function getKlineChartOption(options = {}) {
  const {
    isDark = false,
    showMA = true,
    maParams = [5, 10, 20],
    showVolume = true,
    showLimitLines = false,
    limitUp = null,
    limitDown = null,
    prevClose = null,
  } = options

  // 专业配色方案
  const colors = {
    bg: '#000000', // 纯黑背景
    text: '#8a8e99',
    grid: '#1a1e27',
    up: '#ef4444', // 红色上涨
    down: '#10b981', // 绿色下跌
    ma5: '#ffffff', // 白色（同花顺风格）
    ma10: '#f59e0b', // 黄色（同花顺风格）
    ma20: '#ec4899', // 紫色（同花顺风格）
    avgPrice: '#f59e0b', // 橙色均价线
    limitUp: '#ef4444',
    limitDown: '#10b981',
    prevClose: '#6b7280',
  }

  const baseOption = {
    backgroundColor: colors.bg,
    animation: false, // 禁用动画提升性能
    // 十字标线配置
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#888',
          width: 1,
          type: 'dashed'
        },
        lineStyle: {
          color: '#888',
          width: 1,
          type: 'dashed'
        },
        label: {
          backgroundColor: '#374151',
          color: '#ffffff',
          fontSize: 10,
          padding: [4, 6],
          borderRadius: 3
        }
      },
      backgroundColor: 'rgba(30, 30, 46, 0.9)',
      borderColor: '#374151',
      borderWidth: 1,
      textStyle: {
        color: '#d1d5db',
        fontSize: 11
      },
      padding: [8, 10],
      formatter: function (params) {
        if (!params || params.length === 0) return ''
        const date = params[0].axisValue || ''
        let html = `<div style="font-weight:600;margin-bottom:4px;">${date}</div>`
        params.forEach(item => {
          if (item.seriesType === 'candlestick' && item.data && item.data[0] !== null) {
            const [open, close, low, high] = item.data
            html += `<div>开: ${open} 收: ${close}</div>`
            html += `<div>高: ${high} 低: ${low}</div>`
          } else if (item.seriesName && item.seriesName.startsWith('MA') && item.data !== null && item.data !== '-') {
            html += `<div>${item.seriesName}: ${item.data}</div>`
          }
        })
        return html
      }
    },
    // 全局axisPointer联动
    axisPointer: {
      link: [{ xAxisIndex: 'all' }],
      show: true,
      snap: true,
      triggerOn: 'mousemove|click',
      label: {
        backgroundColor: '#374151'
      }
    },
    grid: [
      {
        left: '3%',
        right: '3%',
        top: '12%',
        height: showVolume ? '55%' : '75%',
        borderWidth: 0,
      },
      {
        left: '3%',
        right: '3%',
        top: '72%',
        height: '18%',
        borderWidth: 0,
      },
    ],
    xAxis: [
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: true,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      {
        type: 'category',
        gridIndex: 1,
        boundaryGap: true,
        axisLine: {
          lineStyle: { color: colors.grid },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: colors.text,
          fontSize: 10,
          interval: 'auto',
          showMinLabel: true,
          showMaxLabel: true,
        },
        splitLine: {
          show: false,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: colors.text,
          fontSize: 10,
          inside: false,
        },
        splitLine: {
          lineStyle: {
            color: colors.grid,
            width: 1,
          },
        },
      },
      {
        type: 'value',
        gridIndex: 1,
        scale: true,
        splitNumber: 2,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false, // 隐藏成交量Y轴标签
        },
        splitLine: {
          lineStyle: {
            color: colors.grid,
            width: 1,
          },
        },
      },
    ],
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: [0, 1],
        start: 0,
        end: 100,
        minSpan: 5,
      },
    ],
    series: [],
  }

  return baseOption
}

/**
 * 生成完整的交易时间轴（9:30-15:00）
 * @param {string} interval - 时间间隔 '1m' 或 '5m'
 * @returns {Array} 完整的时间点数组
 */
export function generateFullTradingTimeAxis(interval = '5m', withStartPoint = false) {
  const timePoints = []
  const intervalMinutes = interval === '1m' ? 1 : 5

  // 上午时段: 09:30 - 11:30
  // start from 30 + interval (e.g. 09:31 or 09:35) to ensure equal count with afternoon
  let hour = 9
  let minute = withStartPoint ? 30 : (30 + intervalMinutes)

  while (hour < 11 || (hour === 11 && minute <= 30)) {
    timePoints.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
    minute += intervalMinutes
    if (minute >= 60) {
      hour += 1
      minute = minute % 60
    }
  }

  // 下午时段: 
  // withStartPoint=true: 13:00 开始
  // 1分钟数据从13:01开始
  // 5分钟数据从13:05开始
  hour = 13
  minute = withStartPoint ? 0 : (interval === '1m' ? 1 : 5)

  while (hour < 15 || (hour === 15 && minute === 0)) {
    timePoints.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
    minute += intervalMinutes
    if (minute >= 60) {
      hour += 1
      minute = minute % 60
    }
    if (hour === 15 && minute > 0) break
  }

  return timePoints
}


/**
 * 将实际K线数据映射到完整时间轴
 * @param {Array} klineData - 实际K线数据
 * @param {Array} fullTimeAxis - 完整时间轴
 * @returns {Object} 映射后的数据 { values, volumes, avgPrices }
 */
export function mapDataToFullTimeAxis(klineData, fullTimeAxis) {
  const dataMap = new Map()

  // 将实际数据按时间建立映射
  klineData.forEach(item => {
    let timeLabel = item.time || item.date
    if (typeof timeLabel === 'string') {
      const timeMatch = timeLabel.match(/(\d{2}):(\d{2})/)
      if (timeMatch) {
        const extractedTime = `${timeMatch[1]}:${timeMatch[2]}`
        dataMap.set(extractedTime, item)
      }
    }
  })

  // 按完整时间轴填充数据
  const values = []
  const volumes = []

  fullTimeAxis.forEach(time => {
    let item = dataMap.get(time)

    if (item) {
      // 有数据：填充实际值
      values.push([
        parseFloat(item.open || 0),
        parseFloat(item.close || 0),
        parseFloat(item.low || 0),
        parseFloat(item.high || 0)
      ])
      volumes.push({
        value: parseFloat(item.volume || 0),
        itemStyle: {
          color: parseFloat(item.close || 0) >= parseFloat(item.open || 0) ? '#ef4444' : '#10b981'
        }
      })
    } else {
      // 无数据：填充占位符
      values.push([null, null, null, null])
      volumes.push({ value: 0, itemStyle: { color: 'transparent' } })
    }
  })

  return { values, volumes }
}

/**
 * 计算分时均价线（基于完整时间轴）
 * 分时均价 = 累计成交额 / 累计成交量
 * 注意：成交额单位可能是"分"，需要除以100转换为"元"
 * @param {Array} klineData - K线数据
 * @param {Array} fullTimeAxis - 完整时间轴
 * @returns {Array} 分时均价数据（与fullTimeAxis长度一致）
 */
export function calculateIntradayAvgPriceWithFullAxis(klineData, fullTimeAxis) {
  const dataMap = new Map()

  // 将实际数据按时间建立映射
  klineData.forEach(item => {
    let timeLabel = item.time || item.date
    if (typeof timeLabel === 'string') {
      const timeMatch = timeLabel.match(/(\d{2}):(\d{2})/)
      if (timeMatch) {
        const extractedTime = `${timeMatch[1]}:${timeMatch[2]}`
        dataMap.set(extractedTime, item)
      }
    }
  })

  const avgPrices = []
  let cumulativeAmount = 0
  let cumulativeVolume = 0
  let debugCount = 0

  fullTimeAxis.forEach(time => {
    let item = dataMap.get(time)

    if (item) {
      const closePrice = parseFloat(item.close)
      const shouldSkip = (closePrice === 0 || isNaN(closePrice))

      if (!shouldSkip) {
        const providedAvgPrice = normalizeIntradayAvgPrice(parseFloat(item.avg_price), closePrice)
        if (!Number.isNaN(providedAvgPrice) && Number.isFinite(providedAvgPrice) && providedAvgPrice > 0) {
          avgPrices.push(parseFloat(providedAvgPrice.toFixed(4)))
          debugCount++
          return
        }

        // 成交额：优先使用 amount，否则用 close * volume 估算
        let amount = parseFloat(item.amount || 0)
        const volume = parseFloat(item.volume || 0)

        // 如果没有 amount 数据，使用 close * volume 作为估算
        if (!amount && volume > 0) {
          amount = closePrice * volume
        }

        // 如果成交额看起来是"分"为单位，转换为"元"
        // 判断逻辑：如果 amount/volume 远大于股价（超过10倍），说明单位是"分"
        if (volume > 0 && closePrice > 0) {
          const impliedPrice = amount / volume
          if (impliedPrice > closePrice * 10) {
            // 单位是"分"，需要除以100
            amount = amount / 100
          }
        }

        cumulativeAmount += amount
        cumulativeVolume += volume

        if (cumulativeVolume > 0) {
          const avgPrice = cumulativeAmount / cumulativeVolume
          const roundedPrice = parseFloat(avgPrice.toFixed(4))
          if (!isNaN(roundedPrice) && isFinite(roundedPrice) && roundedPrice > 0) {
            avgPrices.push(roundedPrice)
            debugCount++
          } else {
            avgPrices.push(null)
          }
        } else {
          avgPrices.push(null)
        }
      } else {
        avgPrices.push(null)
      }
    } else {
      // 无数据时间点：保持上一个有效均价或null
      avgPrices.push(null)
    }
  })

  console.log('📊 calculateIntradayAvgPriceWithFullAxis:', {
    inputDataLength: klineData.length,
    fullTimeAxisLength: fullTimeAxis.length,
    validAvgPriceCount: debugCount,
    firstFiveAvgPrices: avgPrices.slice(0, 5).filter(v => v !== null),
    sampleData: klineData.slice(0, 2).map(k => ({ time: k.time, close: k.close, volume: k.volume, amount: k.amount }))
  })

  return avgPrices
}

/**
 * 规范化后端返回的黄线价格。
 * 某些数据源会把 avg_price 以“分”返回，而收盘价仍然是“元”，
 * 这会导致黄线数值比股价大 100 倍，最终被画到坐标轴外。
 * 这里只在 avg_price 明显高于当前价一个数量级时才做 /100 修正，
 * 避免误伤正常数据。
 * @param {number} rawAvgPrice - 后端原始均价
 * @param {number} referencePrice - 当前K线收盘价，用作量级参考
 * @returns {number} 归一化后的均价
 */
function normalizeIntradayAvgPrice(rawAvgPrice, referencePrice) {
  if (Number.isNaN(rawAvgPrice) || !Number.isFinite(rawAvgPrice) || rawAvgPrice <= 0) {
    return rawAvgPrice
  }

  if (Number.isNaN(referencePrice) || !Number.isFinite(referencePrice) || referencePrice <= 0) {
    return rawAvgPrice
  }

  if (rawAvgPrice > referencePrice * 10) {
    return rawAvgPrice / 100
  }

  return rawAvgPrice
}

/**
 * 计算分时均价线
 * 分时均价 = 累计成交额 / 累计成交量
 * 注意：成交额单位可能是"分"，需要除以100转换为"元"
 * @param {Array} klineData - 5分钟K线数据
 * @returns {Array} 分时均价数据
 */
export function calculateIntradayAvgPrice(klineData) {
  if (!Array.isArray(klineData) || klineData.length === 0) {
    return []
  }

  const avgPrices = []
  let cumulativeAmount = 0  // 累计成交额（元）
  let cumulativeVolume = 0  // 累计成交量（股）

  klineData.forEach((item, index) => {
    const closePrice = parseFloat(item.close)
    const providedAvgPrice = normalizeIntradayAvgPrice(parseFloat(item.avg_price), closePrice)
    if (!Number.isNaN(providedAvgPrice) && Number.isFinite(providedAvgPrice) && providedAvgPrice > 0) {
      avgPrices.push(parseFloat(providedAvgPrice.toFixed(4)))
      return
    }

    // 检查是否应该跳过这根K线
    // 1. 如果close为0且是最后一根K线，跳过
    // 2. 如果close为0且下一根K线不存在，跳过
    const shouldSkip = (closePrice === 0 || isNaN(closePrice)) &&
      (index === klineData.length - 1 || !klineData[index + 1])

    if (shouldSkip) {
      // 不添加均价数据，保持与K线数据一致
      return
    }

    // 获取当前5分钟的成交额和成交量
    // 成交额可能是"分"为单位，需要除以100转换为"元"
    let amount = parseFloat(item.amount || 0)
    const volume = parseFloat(item.volume || 0)

    // 如果成交额看起来是"分"为单位（通常很大的数字），转换为"元"
    // 简单判断：如果amount / volume > 1000，说明可能是"分"
    if (volume > 0 && amount / volume > 1000) {
      amount = amount / 100
    }

    // 累加
    cumulativeAmount += amount
    cumulativeVolume += volume

    // 计算分时均价
    if (cumulativeVolume > 0) {
      const avgPrice = cumulativeAmount / cumulativeVolume
      const roundedPrice = parseFloat(avgPrice.toFixed(4))
      // 确保价格是有效数字且在合理范围内
      if (!isNaN(roundedPrice) && isFinite(roundedPrice) && roundedPrice > 0) {
        avgPrices.push(roundedPrice)
      } else {
        // 如果计算出的均价无效，使用当前K线的收盘价
        const fallbackClose = parseFloat(item.close || item.open || 0)
        avgPrices.push(fallbackClose > 0 ? fallbackClose : '-')
      }
    } else {
      // 如果没有成交量，使用当前K线的收盘价
      const fallbackClose = parseFloat(item.close || item.open || 0)
      avgPrices.push(fallbackClose > 0 ? fallbackClose : '-')
    }
  })

  return avgPrices
}

/**
 * 计算MA均线数据
 * @param {Array} data - K线数据 [[time, open, close, low, high, volume], ...]
 * @param {number} period - MA周期
 * @returns {Array} MA数据
 */
export function calculateMA(data, period) {
  const result = []
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push('-')
      continue
    }
    let sum = 0
    for (let j = 0; j < period; j++) {
      sum += data[i - j][2] // close price
    }
    result.push((sum / period).toFixed(2))
  }
  return result
}

/**
 * 格式化K线数据为ECharts格式
 * @param {Array} klineData - 原始K线数据
 * @param {string} date - 日期 (YYYY-MM-DD)，用于5分钟K线
 * @returns {Object} 格式化后的数据 { categoryData, values, volumes, avgPrices, sourceItems }
 */
export function formatKlineDataForECharts(klineData, date = null) {
  if (!Array.isArray(klineData) || klineData.length === 0) {
    return { categoryData: [], values: [], volumes: [], avgPrices: [], sourceItems: [] }
  }

  const categoryData = []
  const values = []
  const volumes = []
  const avgPrices = []
  const sourceItems = []

  klineData.forEach((item, index) => {
    // 1. 先检查数据类型和有效性
    let timeLabel = item.time || item.date

    // Debug: 打印前3条数据的原始时间格式
    if (index < 3) {
      console.log(`📅 Raw time[${index}]:`, timeLabel, typeof timeLabel)
    }

    const volume = parseFloat(item.volume || 0)
    const open = parseFloat(item.open || 0)
    const close = parseFloat(item.close || 0)

    // 判断是否为日K线（日期格式为 YYYY-MM-DD，没有时间部分）
    const isDailyKline = typeof timeLabel === 'string' && timeLabel.match(/^\d{4}-\d{2}-\d{2}$/)

    // 对于日K线：检查停牌（volume=0 且 open=close，或者 suspended=true）
    // 停牌的日K线直接跳过，不添加到图表中
    if (isDailyKline) {
      const isSuspended = item.suspended === true || (volume === 0 && open === close)
      if (isSuspended) {
        return // 日K线停牌：直接跳过，不保留时间点
      }
    }

    // 2. 提取时间标签（只有非停牌的数据才会执行到这里）
    let extractedTime = timeLabel
    if (typeof timeLabel === 'string') {
      // 匹配 HH:MM 格式（5分钟K线）
      if (timeLabel.match(/^\d{2}:\d{2}$/)) {
        extractedTime = timeLabel
      }
      // 匹配 YYYY-MM-DD HH:MM:SS 或 YYYY-MM-DD HH:MM 格式（1分钟K线）
      else if (timeLabel.includes(' ')) {
        // 提取时间部分 HH:MM
        const parts = timeLabel.split(' ')
        if (parts.length >= 2) {
          extractedTime = parts[1].substring(0, 5) // 只取 HH:MM
          if (index < 3) {
            console.log(`  ✂️ Extracted time[${index}]:`, extractedTime)
          }
        }
      }
      // 匹配 YYYY-MM-DD 格式（日K线）
      else if (isDailyKline) {
        // 转换为 MM-DD
        const parts = timeLabel.split('-')
        extractedTime = `${parts[1]}-${parts[2]}`
      }
    }

    // 3. 添加时间标签（对于分时线，保留所有时间点以保持连续性）
    categoryData.push(extractedTime)
    // 记录实际进入图表的数据项。
    // 日K线遇到停牌会被提前 return，因此这里的 sourceItems 与最终图表索引一一对应，
    // 后续点击映射、涨停标记都必须基于它，不能再依赖原始 klineData 的索引。
    sourceItems.push(item)

    // 处理盘中数据：如果close为0或无效，使用下一根K线的open作为close
    let closePrice = close
    if (closePrice === 0 || isNaN(closePrice) || !isFinite(closePrice)) {
      // 如果有下一根K线，使用下一根的open
      if (index < klineData.length - 1) {
        const nextOpen = parseFloat(klineData[index + 1].open)
        if (nextOpen > 0 && !isNaN(nextOpen) && isFinite(nextOpen)) {
          closePrice = nextOpen
        } else {
          // 无效数据：添加空数据，保留时间点
          values.push([null, null, null, null])
          volumes.push({ value: 0, itemStyle: { color: '#666' } })
          avgPrices.push(null)
          return
        }
      } else {
        // 最后一根K线，如果close为0，添加空数据
        values.push([null, null, null, null])
        volumes.push({ value: 0, itemStyle: { color: '#666' } })
        avgPrices.push(null)
        return
      }
    }

    // --- 数据有效，添加正常数据 ---
    values.push([
      parseFloat(item.open),
      closePrice,
      parseFloat(item.low),
      parseFloat(item.high),
    ])

    // 3. 添加成交量数据
    volumes.push({
      value: volume,
      itemStyle: {
        color: closePrice >= parseFloat(item.open)
          ? 'rgba(239, 68, 68, 0.5)'
          : 'rgba(16, 185, 129, 0.5)',
      },
    })

    // 4. 添加均价数据（5分钟图）
    if (item.avg_price !== null && item.avg_price !== undefined) {
      avgPrices.push(normalizeIntradayAvgPrice(parseFloat(item.avg_price), closePrice))
    } else {
      avgPrices.push('-')
    }
  })

  return { categoryData, values, volumes, avgPrices, sourceItems }
}

/**
 * 创建K线系列配置
 * @param {Object} colors - 颜色配置
 * @returns {Object} K线系列配置
 */
export function createCandlestickSeries(colors) {
  return {
    type: 'candlestick',
    name: 'K线',
    xAxisIndex: 0,
    yAxisIndex: 0,
    itemStyle: {
      color: colors.up,
      color0: colors.down,
      borderColor: colors.up,
      borderColor0: colors.down,
    },
    barWidth: '60%',
  }
}

/**
 * 创建MA线系列配置
 * @param {number} period - MA周期
 * @param {string} color - 线条颜色
 * @returns {Object} MA线系列配置
 */
export function createMASeries(period, color) {
  return {
    type: 'line',
    name: `MA${period}`,
    xAxisIndex: 0,
    yAxisIndex: 0,
    smooth: false,
    symbol: 'none',
    lineStyle: {
      width: 1,
      color: color,
    },
  }
}

/**
 * 创建均价线系列配置
 * @param {string} color - 线条颜色
 * @returns {Object} 均价线系列配置
 */
export function createAvgPriceSeries(color) {
  return {
    type: 'line',
    name: '均价',
    xAxisIndex: 0,
    yAxisIndex: 0,
    smooth: false,
    symbol: 'none',
    lineStyle: {
      width: 2,
      color: color,
    },
  }
}

/**
 * 创建成交量系列配置
 * @returns {Object} 成交量系列配置
 */
export function createVolumeSeries() {
  return {
    type: 'bar',
    name: '成交量',
    xAxisIndex: 1,
    yAxisIndex: 1,
    barWidth: '60%',
  }
}

/**
 * 创建标记线配置（涨跌停线、前收盘价线）
 * @param {Object} options - 配置选项
 * @returns {Object} 标记线配置
 */
export function createMarkLines(options = {}) {
  const {
    limitUp = null,
    limitDown = null,
    prevClose = null,
    colors = {},
  } = options

  const markLineData = []

  if (limitUp) {
    markLineData.push({
      yAxis: limitUp,
      lineStyle: {
        color: colors.limitUp || '#ef4444',
        type: 'dashed',
        width: 1,
      },
      label: {
        formatter: '涨停',
        position: 'end',
        color: colors.limitUp || '#ef4444',
        fontSize: 10,
      },
    })
  }

  if (limitDown) {
    markLineData.push({
      yAxis: limitDown,
      lineStyle: {
        color: colors.limitDown || '#10b981',
        type: 'dashed',
        width: 1,
      },
      label: {
        formatter: '跌停',
        position: 'end',
        color: colors.limitDown || '#10b981',
        fontSize: 10,
      },
    })
  }

  if (prevClose) {
    markLineData.push({
      yAxis: prevClose,
      lineStyle: {
        color: colors.prevClose || '#6b7280',
        type: 'dashed',
        width: 1,
      },
      label: {
        formatter: '前收',
        position: 'end',
        color: colors.prevClose || '#6b7280',
        fontSize: 10,
      },
    })
  }

  return {
    silent: true,
    symbol: 'none',
    data: markLineData,
  }
}

/**
 * 获取价格的最小变动单位
 * A股价格档位规则：
 * - 0.01元 ≤ 价格 < 1元：最小变动单位 0.001元
 * - 1元 ≤ 价格：最小变动单位 0.01元
 * @param {number} price - 价格
 * @returns {number} 最小变动单位
 */
function getTickSize(price) {
  if (price < 1.0) {
    return 0.001
  } else {
    return 0.01
  }
}

/**
 * 获取股票的涨跌幅限制比例
 * A股板块代码规则：
 * - 主板：60xxxx（上海）、000xxx/001xxx（深圳）
 * - 科创板：688xxx（上海）
 * - 创业板：30xxxx（深圳，包括300/301/302/303等）
 * - 北交所：920xxx
 * @param {string} stockCode - 股票代码
 * @returns {Object} {upRatio, downRatio} 涨跌幅限制
 */
function getLimitRatio(stockCode) {
  // 去掉可能的市场后缀（.SZ/.SH）
  const code = stockCode.includes('.') ? stockCode.split('.')[0] : stockCode

  // 判断是否为ST股票（涨跌幅限制5%）
  const isST = code.toUpperCase().includes('ST') || code.startsWith('*')

  // 判断是否为科创板（688开头，涨跌幅限制20%）
  const isKCB = code.startsWith('688')

  // 判断是否为创业板（30开头，涨跌幅限制20%）
  const isCYB = code.startsWith('30')

  // 判断是否为北交所（920开头，涨跌幅限制30%）
  const isBSE = code.startsWith('920')

  if (isST) {
    return { upRatio: 0.05, downRatio: 0.05 }
  } else if (isKCB || isCYB) {
    return { upRatio: 0.20, downRatio: 0.20 }
  } else if (isBSE) {
    return { upRatio: 0.30, downRatio: 0.30 }
  } else {
    // 主板和中小板（涨跌幅限制10%）
    return { upRatio: 0.10, downRatio: 0.10 }
  }
}

/**
 * 计算涨跌停价格（使用后端相同的算法）
 * @param {number} prevClose - 前收盘价
 * @param {string} stockCode - 股票代码
 * @returns {Object} 涨跌停价格 {limitUp, limitDown}
 */
export function calculateLimitPrices(prevClose, stockCode = '') {
  if (!prevClose || prevClose <= 0) return { limitUp: null, limitDown: null }

  const { upRatio, downRatio } = getLimitRatio(stockCode)

  // 计算理论涨停价
  const theoreticalLimitUp = prevClose * (1 + upRatio)
  const tickUp = getTickSize(theoreticalLimitUp)
  const limitUp = Math.round(theoreticalLimitUp / tickUp) * tickUp

  // 计算理论跌停价
  const theoreticalLimitDown = prevClose * (1 - downRatio)
  const tickDown = getTickSize(theoreticalLimitDown)
  const limitDown = Math.round(theoreticalLimitDown / tickDown) * tickDown

  // 保留合适的小数位数
  return {
    limitUp: tickUp === 0.001 ? parseFloat(limitUp.toFixed(3)) : parseFloat(limitUp.toFixed(2)),
    limitDown: tickDown === 0.001 ? parseFloat(limitDown.toFixed(3)) : parseFloat(limitDown.toFixed(2)),
  }
}

/**
 * 从分时/5分钟数据中提取有效价格，供 Y 轴自适应缩放使用。
 */
function collectIntradayPrices(kline5mData = [], kline1mData = []) {
  const prices = []

  const pushPrice = (value) => {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) prices.push(num)
  }

  ;(Array.isArray(kline5mData) ? kline5mData : []).forEach((item) => {
    pushPrice(item?.open)
    pushPrice(item?.high)
    pushPrice(item?.low)
    pushPrice(item?.close)
  })

  ;(Array.isArray(kline1mData) ? kline1mData : []).forEach((item) => {
    pushPrice(item?.open)
    pushPrice(item?.high)
    pushPrice(item?.low)
    pushPrice(item?.close)
  })

  return prices
}

/**
 * 计算桌面端分时图可见价格区间。
 * 桌面端画布往往很宽，若 Y 轴固定为涨跌停全幅，白线波动会被压平；
 * 这里以前收为中心做对称缩放，并限制在涨跌停范围内。
 */
export function computeIntradayVisiblePriceRange({
  kline5mData = [],
  kline1mData = [],
  prevClose = null,
  limitUp = null,
  limitDown = null,
} = {}) {
  const prices = collectIntradayPrices(kline5mData, kline1mData)
  if (prevClose && prevClose > 0) prices.push(prevClose)
  if (prices.length === 0) {
    return { min: limitDown, max: limitUp }
  }

  const dayHigh = Math.max(...prices)
  const dayLow = Math.min(...prices)
  const center = prevClose && prevClose > 0 ? prevClose : (dayHigh + dayLow) / 2

  const upMove = Math.max(dayHigh - center, 0)
  const downMove = Math.max(center - dayLow, 0)
  const halfRange = Math.max(upMove, downMove)

  // 中文注释：收紧可视振幅与边距，让桌面端分时白线起伏更陡。
  const minHalfRange = center * 0.005
  const paddedHalfRange = Math.max(halfRange, minHalfRange) * 1.02

  let min = center - paddedHalfRange
  let max = center + paddedHalfRange

  if (limitDown != null) min = Math.max(min, limitDown)
  if (limitUp != null) max = Math.min(max, limitUp)

  // 若触板后区间被涨跌停夹住，仍保证最小可视高度
  if (max - min < minHalfRange * 2) {
    const expand = (minHalfRange * 2 - (max - min)) / 2
    min -= expand
    max += expand
    if (limitDown != null) min = Math.max(min, limitDown)
    if (limitUp != null) max = Math.min(max, limitUp)
  }

  const tick = getTickSize(center)
  const decimals = tick === 0.001 ? 3 : 2
  return {
    min: parseFloat(min.toFixed(decimals)),
    max: parseFloat(max.toFixed(decimals)),
  }
}

/**
 * 计算“分时线”专用的可见价格区间。
 * 与候选 K 线不同，分时图只画收盘连线（白线）与均价线（黄线），
 * 不存在影线高低点，因此可以贴着线本身的波动来缩放，让白线像同花顺一样陡。
 * 这里以前收为中心做对称缩放，效果即同花顺的 “+x% / -x%” 对称刻度。
 */
export function computeLinePriceRange({
  prices = [],
  avgPrices = [],
  tradePrices = [],
  prevClose = null,
  limitUp = null,
  limitDown = null,
} = {}) {
  const all = []
  const pushValid = (value) => {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) all.push(num)
  }
  prices.forEach(pushValid)
  avgPrices.forEach(pushValid)
  tradePrices.forEach(pushValid)

  if (all.length === 0) {
    return { min: limitDown, max: limitUp }
  }

  const dayHigh = Math.max(...all)
  const dayLow = Math.min(...all)
  const center = prevClose && prevClose > 0 ? prevClose : (dayHigh + dayLow) / 2

  // 以前收为中心、取最大偏离做对称半幅，保证 0% 线居中（同花顺风格）
  const maxDeviation = Math.max(...all.map((v) => Math.abs(v - center)))
  const minHalfRange = center * 0.004
  const paddedHalfRange = Math.max(maxDeviation, minHalfRange) * 1.06

  let min = center - paddedHalfRange
  let max = center + paddedHalfRange

  if (limitDown != null) min = Math.max(min, limitDown)
  if (limitUp != null) max = Math.min(max, limitUp)

  const tick = getTickSize(center)
  const decimals = tick === 0.001 ? 3 : 2
  return {
    min: parseFloat(min.toFixed(decimals)),
    max: parseFloat(max.toFixed(decimals)),
  }
}

/**
 * 获取桌面端“拆分面板”配置：一个面板只画一类数据，避免叠加导致空白与白线被压平。
 * 上方价格 grid + 下方成交量 grid，共两个 grid。
 * @param {('line'|'candle')} kind - line: 分时白线+黄线+1分量；candle: 5分K柱+5分量
 * @param {Object} options
 */
export function getDesktopSplitOption(kind = 'line', options = {}) {
  const {
    isDark = false,
    limitUp = null,
    limitDown = null,
    prevClose = null,
  } = options

  const colors = {
    bg: '#000000',
    text: '#8a8e99',
    grid: '#1a1e27',
    up: '#ef4444',
    down: '#10b981',
    whiteLine: '#ffffff',
    yellowLine: '#fbbf24',
  }

  // 关键时间标签，仅在成交量 grid 的底部 X 轴显示
  const timeLabelFormatter = function (value) {
    const targetTimes = ['09:30', '10:00', '10:30', '11:00', '11:30', '13:30', '14:00', '14:30', '15:00']
    return targetTimes.includes(value) ? value : ''
  }

  const baseOption = {
    backgroundColor: colors.bg,
    animation: false,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
    },
    axisPointer: {
      link: [{ xAxisIndex: 'all' }],
      show: true,
      snap: true,
    },
    grid: [
      // 0: 价格
      { left: '46', right: '52', top: '8%', height: '64%', borderWidth: 0 },
      // 1: 成交量
      { left: '46', right: '52', top: '78%', height: '18%', borderWidth: 0 },
    ],
    xAxis: [
      // 0: 价格 grid 的类目轴（隐藏标签，刻度在底部成交量轴）
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: kind === 'candle',
        axisLine: { show: true, lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: true, lineStyle: { color: colors.grid, type: 'dashed' } },
        data: [],
      },
      // 1: 成交量 grid 的类目轴（显示关键时间）
      {
        type: 'category',
        gridIndex: 1,
        boundaryGap: kind === 'candle',
        axisLine: { lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: {
          color: colors.text,
          fontSize: 10,
          rotate: 0,
          formatter: timeLabelFormatter,
          interval: 0,
          showMinLabel: false,
          showMaxLabel: false,
        },
        splitLine: { show: false },
        data: [],
      },
    ],
    yAxis: [
      // 0: 价格轴（左）
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: colors.text, fontSize: 10, formatter: (v) => v.toFixed(2) },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } },
        min: limitDown,
        max: limitUp,
      },
      // 1: 成交量轴
      {
        type: 'value',
        gridIndex: 1,
        scale: false,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } },
      },
      // 2: 涨跌幅轴（右，基于前收换算百分比）
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        position: 'right',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: function (value) {
            if (!prevClose || prevClose <= 0) return colors.text
            const pct = (value - prevClose) / prevClose * 100
            if (pct > 0.01) return '#ef4444'
            if (pct < -0.01) return '#10b981'
            return colors.text
          },
          fontSize: 10,
          formatter: function (value) {
            if (!prevClose || prevClose <= 0) return ''
            const pct = (value - prevClose) / prevClose * 100
            return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'
          },
        },
        splitLine: { show: false },
        min: limitDown,
        max: limitUp,
      },
    ],
    series: [],
  }

  if (kind === 'candle') {
    baseOption.series = [
      {
        name: '5minK',
        type: 'candlestick',
        xAxisIndex: 0,
        yAxisIndex: 0,
        itemStyle: {
          color: colors.up,
          color0: colors.down,
          borderColor: colors.up,
          borderColor0: colors.down,
        },
        barWidth: '60%',
        data: [],
      },
      {
        name: '5minVol',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        barWidth: '60%',
        data: [],
      },
    ]
  } else {
    baseOption.series = [
      {
        name: 'Price1min',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        smooth: false,
        showSymbol: false,
        connectNulls: true,
        lineStyle: { color: colors.whiteLine, width: 1.5 },
        data: [],
      },
      {
        name: 'AvgPrice',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        smooth: false,
        showSymbol: false,
        connectNulls: true,
        lineStyle: { color: colors.yellowLine, width: 1 },
        data: [],
      },
      {
        name: '1minVol',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        barWidth: '60%',
        data: [],
      },
    ]
  }

  return baseOption
}

/**
 * 创建涨跌停标记
 * @param {Array} klineData - 原始K线数据
 * @returns {Array} 标记数据
 */
export function createLimitMarkers(klineData) {
  if (!Array.isArray(klineData)) return []

  const markers = []

  klineData.forEach((item, index) => {
    // 涨停/跌停标记必须使用 dataIndex 对应的数值坐标。
    // 优先使用后端标识，同时容错兼容涨跌幅达到 10% 的情况
    const isUp = Boolean(
      item.is_limit_up ||
      (Number(item.change_percent) >= 9.8 && Math.abs(Number(item.close) - Number(item.high)) < 0.02)
    )
    const isDown = Boolean(
      item.is_limit_down ||
      (Number(item.change_percent) <= -9.8 && Math.abs(Number(item.close) - Number(item.low)) < 0.02)
    )

    if (isUp) {
      const highPrice = parseFloat(item.high || item.close)
      markers.push({
        name: '涨停',
        xAxis: index,
        yAxis: highPrice,
        symbol: 'arrow',
        symbolSize: 3, // 缩小50%，精致不遮挡K线柱体
        symbolRotate: 0,
        symbolOffset: [0, -5], // 紧贴最高价上方
        itemStyle: {
          color: '#fbbf24',
          borderWidth: 0,
        },
      })
    }

    if (isDown) {
      const lowPrice = parseFloat(item.low || item.close)
      markers.push({
        name: '跌停',
        xAxis: index,
        yAxis: lowPrice,
        symbol: 'arrow',
        symbolSize: 3, // 缩小50%，精致不遮挡K线柱体
        symbolRotate: 180,
        symbolOffset: [0, 5], // 紧贴最低价下方
        itemStyle: {
          color: '#fbbf24',
          borderWidth: 0,
        },
      })
    }
  })

  return markers
}

/**
 * 获取移动端5分钟K线图配置 (分时叠加模式 + 双成交量)
 * @param {Object} options - 配置选项
 * @param {string} options.mode - 显示模式: '1m' | '5m' | 'hybrid'
 *   - '1m': 隐藏5分柱状图，显示1分白线、黄线和成交量
 *   - '5m': 隐藏1分白线，显示5分K线、黄线和成交量
 *   - 'hybrid': 全部显示（叠加模式）
 */
export function getMobile5MinOption(options = {}) {
  const {
    isDark = false,
    limitUp = null,
    limitDown = null,
    prevClose = null,
    mode = 'hybrid', // '1m' | '5m' | 'hybrid'
  } = options

  // 颜色配置
  const colors = {
    bg: '#000000',
    text: '#8a8e99',
    grid: '#1a1e27',
    up: '#ef4444',
    down: '#10b981',
    whiteLine: '#ffffff', // 1分钟线
    yellowLine: '#fbbf24', // 均价线/MA
    limitUp: '#ef4444',
    limitDown: '#10b981',
    prevClose: '#6b7280',
  }

  // 根据模式决定系列样式
  // mode = '1m': 隐藏5分K线，保留5分成交量、1分白线、黄线、1分成交量
  // mode = '5m': 隐藏1分白线，保留5分K线、5分成交量、黄线、1分成交量
  // mode = 'hybrid': 全部显示
  const show5minK = mode !== '1m' // 1分模式隐藏5分K线
  const showPrice1min = mode !== '5m' // 5分模式隐藏1分白线
  // 黄线（均价线）始终显示
  // 5分成交量和1分成交量始终显示

  return {
    backgroundColor: colors.bg,
    animation: false,
    // 十字标线配置
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#888',
          width: 1,
          type: 'dashed'
        },
        lineStyle: {
          color: '#888',
          width: 1,
          type: 'dashed'
        },
        label: {
          backgroundColor: '#374151',
          color: '#ffffff',
          fontSize: 10,
          padding: [4, 6],
          borderRadius: 3
        }
      },
      backgroundColor: 'rgba(30, 30, 46, 0.9)',
      borderColor: '#374151',
      borderWidth: 1,
      textStyle: {
        color: '#d1d5db',
        fontSize: 11
      },
      padding: [8, 10],
      formatter: function (params) {
        if (!params || params.length === 0) return ''
        const time = params[0].axisValue || ''
        let html = `<div style="font-weight:600;margin-bottom:4px;">${time}</div>`
        params.forEach(item => {
          if (item.seriesName === '5minK' && item.data && item.data[0] !== null) {
            const [open, close, low, high] = item.data
            html += `<div>开: ${open} 高: ${high}</div>`
            html += `<div>低: ${low} 收: ${close}</div>`
          } else if (item.seriesName === 'Price1min' && item.data !== null) {
            html += `<div>分时: ${item.data}</div>`
          } else if (item.seriesName === 'AvgPrice' && item.data !== null) {
            html += `<div>均价: ${item.data}</div>`
          }
        })
        return html
      }
    },
    // 全局axisPointer联动
    axisPointer: {
      link: [{ xAxisIndex: 'all' }],
      show: true,
      snap: true,
      triggerOn: 'mousemove|click',
      label: {
        backgroundColor: '#374151'
      }
    },
    // 三个Grid布局
    grid: [
      {
        left: '60', right: '55', top: '10%', height: '46%', borderWidth: 0,
      },
      {
        left: '60', right: '55', top: '59%', height: '11%', borderWidth: 0,
      },
      {
        left: '60', right: '55', top: '73%', height: '11%', borderWidth: 0,
      }
    ],
    // 多个X轴
    xAxis: [
      // 0: 5分钟分类轴 (Grid 0 & 1 & 2) - 隐藏的具体刻度，主要用于占位
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        data: [] // 后续填充 5分钟时间点
      },
      // 1: 5分钟分类轴 (Grid 1 - 5分钟成交量)
      {
        type: 'category',
        gridIndex: 1,
        boundaryGap: true,
        axisLine: { lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        data: [] // 5分钟时间点
      },
      // 2: 1分钟分类轴 (Grid 0 - 用于白线/黄线) - 隐藏
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        data: [] // 1分钟时间点
      },
      // 3: 1分钟分类轴 (Grid 2 - 1分钟成交量)
      {
        type: 'category',
        gridIndex: 2,
        boundaryGap: true,
        axisLine: { lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: {
          color: colors.text,
          fontSize: 9,
          rotate: 0,
          // 使用formatter来精确控制显示哪些时间点
          // 注意：11:30和13:01在图表上相邻，只显示11:30
          formatter: function (value) {
            // 只显示特定时间点
            const targetTimes = ['09:30', '10:00', '10:30', '11:00', '11:30', '13:30', '14:00', '14:30', '15:00']
            return targetTimes.includes(value) ? value : ''
          },
          interval: 0, // 显示所有标签，但通过formatter过滤
          showMinLabel: false,
          showMaxLabel: false
        },
        splitLine: { show: false },
        data: [] // 1分钟时间点
      }
    ],
    yAxis: [
      // 0: Grid 0: 价格轴 (左侧)
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: colors.text, fontSize: 10, inside: false },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } },
        min: limitDown,
        max: limitUp
      },
      // 1: Grid 1: 5-min Volume
      {
        type: 'value',
        gridIndex: 1,
        scale: false,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } }
      },
      // 2: Grid 2: 1-min Volume
      {
        type: 'value',
        gridIndex: 2,
        scale: false,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } }
      },
      // 3: Grid 0: 涨跌幅轴 (右侧)
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        position: 'right',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: function (value) {
            if (!prevClose || prevClose <= 0) return colors.text
            const pct = (value - prevClose) / prevClose * 100
            if (pct > 0.01) return '#ef4444'
            if (pct < -0.01) return '#10b981'
            return colors.text
          },
          fontSize: 10,
          inside: false,
          formatter: function (value) {
            if (!prevClose || prevClose <= 0) return ''
            if (limitUp && Math.abs(value - limitUp) < 0.001) return ''
            if (limitDown && Math.abs(value - limitDown) < 0.001) return ''
            const pct = (value - prevClose) / prevClose * 100
            return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'
          }
        },
        splitLine: { show: false },
        axisPointer: {
          show: true,
          label: {
            show: true,
            formatter: function (params) {
              if (!prevClose || prevClose <= 0) return ''
              const pct = (params.value - prevClose) / prevClose * 100
              return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'
            },
            backgroundColor: '#374151'
          }
        },
        min: limitDown,
        max: limitUp
      }
    ],
    series: [
      // 0: 5-min Candles - 根据mode决定是否显示
      {
        name: '5minK',
        type: 'candlestick',
        xAxisIndex: 0,
        yAxisIndex: 0,
        itemStyle: {
          color: show5minK ? colors.up : 'transparent',
          color0: show5minK ? colors.down : 'transparent',
          borderColor: show5minK ? colors.up : 'transparent',
          borderColor0: show5minK ? colors.down : 'transparent'
        },
        barWidth: '60%',
        data: []
      },
      // 1: 5-min Volume - 始终显示
      {
        name: '5minVol',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        barWidth: '60%',
        data: []
      },
      // 2: 1-min White Line (Price) - 根据mode决定是否显示
      {
        name: 'Price1min',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 0, // 映射到同一个价格轴
        smooth: true,
        showSymbol: false,
        connectNulls: true, // 跨越11:30-13:00午休空档连接线段
        lineStyle: {
          color: showPrice1min ? colors.whiteLine : 'transparent',
          width: showPrice1min ? 1 : 0
        },
        data: []
      },
      // 3: 1-min Yellow Line (Avg Price) - 始终显示
      {
        name: 'AvgPrice',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 0,
        smooth: true,
        showSymbol: false,
        connectNulls: true, // 跨越11:30-13:00午休空档连接线段
        lineStyle: { color: colors.yellowLine, width: 1 },
        data: []
      },
      // 4: 1-min Volume - 始终显示
      {
        name: '1minVol',
        type: 'bar',
        xAxisIndex: 3,
        yAxisIndex: 2,
        barWidth: '60%',
        data: []
      },
    ]
  }
}

/**
 * 获取桌面端分时图配置 (5分钟 + 分时 + 双成交量)
 * 显示更多细节（坐标轴标签等）
 * @param {Object} options - 配置选项
 * @param {string} options.mode - 显示模式: '1m' | '5m' | 'hybrid'
 *   - '1m': 隐藏5分K线，显示1分白线、黄线和成交量
 *   - '5m': 隐藏1分白线，显示5分K线、黄线和成交量
 *   - 'hybrid': 全部显示（叠加模式）
 */
export function getDesktopTimeSharingOption(options = {}) {
  const {
    isDark = false,
    limitUp = null,
    limitDown = null,
    prevClose = null,
    mode = 'hybrid', // '1m' | '5m' | 'hybrid'
  } = options

  // 颜色配置 - 桌面端使用深色主题（与移动端一致）
  const colors = {
    bg: '#000000', // 使用黑色背景
    text: '#8a8e99',
    grid: '#1a1e27',
    up: '#ef4444',
    down: '#10b981',
    whiteLine: '#ffffff', // 1分钟线用白色
    yellowLine: '#fbbf24',
    limitUp: '#ef4444',
    limitDown: '#10b981',
    prevClose: '#6b7280',
  }

  // 根据模式决定系列样式（与移动端保持一致）
  // mode = '1m': 隐藏5分K线，保留5分成交量、1分白线、黄线、1分成交量
  // mode = '5m': 隐藏1分白线，保留5分K线、5分成交量、黄线、1分成交量
  // mode = 'hybrid': 全部显示
  const show5minK = mode !== '1m' // 1分模式隐藏5分K线
  const showPrice1min = mode !== '5m' // 5分模式隐藏1分白线
  // 黄线（均价线）始终显示
  // 5分成交量和1分成交量始终显示

  return {
    backgroundColor: colors.bg,
    animation: false,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    // 三个Grid布局
    grid: [
      {
        left: '50', right: '55', top: '30', height: '55%', borderWidth: 0,
      },
      {
        left: '50', right: '55', top: '65%', height: '15%', borderWidth: 0,
      },
      {
        left: '50', right: '55', top: '83%', height: '15%', borderWidth: 0,
      }
    ],
    // 多个X轴
    xAxis: [
      // 0: 5分钟分类轴 (Grid 0)
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: true,
        axisLine: { show: true, lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: { show: false }, // 上方图表不显示时间，时间在最底部
        splitLine: { show: true, lineStyle: { color: colors.grid, type: 'dashed' } },
        data: []
      },
      // 1: 5分钟分类轴 (Grid 1 - 5分钟成交量)
      {
        type: 'category',
        gridIndex: 1,
        boundaryGap: true,
        axisLine: { lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        data: []
      },
      // 2: 1分钟分类轴 (Grid 0 - 用于白线/黄线) - 隐藏
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        data: []
      },
      // 3: 1分钟分类轴 (Grid 2 - 1分钟成交量)
      {
        type: 'category',
        gridIndex: 2,
        boundaryGap: true,
        axisLine: { lineStyle: { color: colors.grid } },
        axisTick: { show: false },
        axisLabel: {
          color: colors.text,
          fontSize: 10,
          rotate: 0,
          // 使用formatter来精确控制显示哪些时间点
          formatter: function (value) {
            // 只显示特定时间点
            const targetTimes = ['09:30', '10:00', '10:30', '11:00', '11:30', '13:30', '14:00', '14:30', '15:00']
            return targetTimes.includes(value) ? value : ''
          },
          interval: 0, // 显示所有标签，但通过formatter过滤
          showMinLabel: false,
          showMaxLabel: false
        },
        splitLine: { show: false },
        data: []
      }
    ],
    yAxis: [
      // 0: Grid 0: 价格轴 (左侧)
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: colors.text, fontSize: 11 },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } },
        min: limitDown,
        max: limitUp
      },
      // 1: Grid 1: 5-min Volume
      {
        type: 'value',
        gridIndex: 1,
        scale: false,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } }
      },
      // 2: Grid 2: 1-min Volume
      {
        type: 'value',
        gridIndex: 2,
        scale: false,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { lineStyle: { color: colors.grid, width: 1 } }
      },
      // 3: Grid 0: 涨跌幅轴 (右侧)
      {
        type: 'value',
        gridIndex: 0,
        scale: true,
        splitNumber: 4,
        position: 'right',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: function (value) {
            if (!prevClose || prevClose <= 0) return colors.text
            const pct = (value - prevClose) / prevClose * 100
            if (pct > 0.01) return '#ef4444'
            if (pct < -0.01) return '#10b981'
            return colors.text
          },
          fontSize: 10,
          inside: false,
          formatter: function (value) {
            if (!prevClose || prevClose <= 0) return ''
            if (limitUp && Math.abs(value - limitUp) < 0.001) return ''
            if (limitDown && Math.abs(value - limitDown) < 0.001) return ''
            const pct = (value - prevClose) / prevClose * 100
            return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'
          }
        },
        splitLine: { show: false },
        axisPointer: {
          show: true,
          label: {
            show: true,
            formatter: function (params) {
              if (!prevClose || prevClose <= 0) return ''
              const pct = (params.value - prevClose) / prevClose * 100
              return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'
            },
            backgroundColor: '#374151'
          }
        },
        min: limitDown,
        max: limitUp
      }
    ],
    series: [
      // 0: 5-min Candles - 根据mode决定是否显示
      {
        name: '5minK',
        type: 'candlestick',
        xAxisIndex: 0,
        yAxisIndex: 0,
        itemStyle: {
          color: show5minK ? colors.up : 'transparent',
          color0: show5minK ? colors.down : 'transparent',
          borderColor: show5minK ? colors.up : 'transparent',
          borderColor0: show5minK ? colors.down : 'transparent'
        },
        barWidth: '60%',
        data: []
      },
      // 1: 5-min Volume - 始终显示
      {
        name: '5minVol',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        barWidth: '60%',
        data: []
      },
      // 2: 1-min Line (Price) - 根据mode决定是否显示
      {
        name: 'Price1min',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 0,
        smooth: true,
        showSymbol: false,
        connectNulls: true, // 跨越11:30-13:00午休空档连接线段
        lineStyle: {
          color: showPrice1min ? colors.whiteLine : 'transparent',
          width: showPrice1min ? 1.5 : 0
        },
        data: []
      },
      // 3: 1-min Avg Price (Yellow Line) - 始终显示
      {
        name: 'AvgPrice',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 0,
        smooth: true,
        showSymbol: false,
        connectNulls: true, // 跨越11:30-13:00午休空档连接线段
        lineStyle: { color: colors.yellowLine, width: 1.5 },
        data: []
      },
      // 4: 1-min Volume - 始终显示
      {
        name: '1minVol',
        type: 'bar',
        xAxisIndex: 3,
        yAxisIndex: 2,
        barWidth: '60%',
        data: []
      },
    ]
  }
}

